/* ==========================================================================
   Redesign — blog listing and article
   Loaded only when rd_blog (snippets/redesign-active.liquid). Deferred.

   One job: build the article's table of contents from the headings that are
   already in the post, and track which one is in view.

   WHY IT IS DONE IN JS AT ALL. The headings live inside article.content, which
   is merchant rich text stored in Shopify — there is no Liquid hook to add an
   id to an h2 inside it, and INVARIANTS §1 forbids rewriting that markup in
   any case. So the ids are added at runtime.

   WHAT IT MAY AND MAY NOT DO. It reads heading text and writes an id. It never
   changes heading text, level, or order, and it never moves a heading in the
   DOM. If you extend this file, keep that line: the outline of 253 indexed
   pages is the thing the whole branch is judged on.

   NOTHING HERE SHIFTS LAYOUT. The TOC starts [hidden] and is only revealed
   once it has its links, so it never renders empty and then grows. CLS is the
   metric this branch is judged on and the rail is above the fold on desktop.
   ========================================================================== */
(function () {
  'use strict';

  var prose = document.getElementById('rd-prose');
  var toc = document.getElementById('rd-toc');
  if (!prose || !toc) return;

  var list = toc.querySelector('.rd-toc__list');
  if (!list) return;

  var heads = Array.prototype.slice.call(prose.querySelectorAll('h2'));

  /* Two is the threshold, not one. A contents box listing a single section is
     noise — the reader can see the whole article is that section. */
  if (heads.length < 2) return;

  var used = Object.create(null);

  function slug(text, index) {
    var base = text.toLowerCase()
      .replace(/[‘’“”]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (!base) base = 'section';
    base = 'rd-' + base.slice(0, 48);
    /* Guides repeat headings ("What we recommend" twice under different
       brands). A duplicate id would make both TOC links jump to the first. */
    if (used[base]) {
      used[base] += 1;
      return base + '-' + used[base];
    }
    used[base] = 1;
    return base;
  }

  var links = [];

  heads.forEach(function (h, i) {
    var text = (h.textContent || '').trim();
    if (!text) return;

    /* Never clobber an id the merchant put there themselves — some guides
       already anchor to their own headings from inside the copy. */
    if (!h.id) h.id = slug(text, i);

    var a = document.createElement('a');
    a.href = '#' + h.id;
    a.textContent = text;
    list.appendChild(a);
    links.push({ a: a, h: h });
  });

  if (!links.length) return;
  toc.hidden = false;

  /* Anchor jumps have to clear the sticky header. scroll-margin-top on the
     heading is what the browser uses for both a click and a page load with a
     #fragment, so it beats intercepting the click. The value mirrors the
     rail's own offset in redesign-blog.css. */
  var headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--rd-header-h'), 10) || 76;
  links.forEach(function (item) {
    item.h.style.scrollMarginTop = (headerH + 24) + 'px';
  });

  /* ------------------------------------------------------------ scrollspy

     THE ACTIVE ITEM IS COMPUTED FROM POSITIONS, NOT FROM isIntersecting.

     The first version trusted the observer's own intersecting flag, with a
     rootMargin of "-100px 0px -70% 0px". That band is a thin strip near the
     top of the viewport, so a heading is "intersecting" only for the moment it
     crosses it — the rest of the time NOTHING is intersecting. The fallback
     then lit the LAST link, so scrolling past heading 1 jumped the highlight
     to the bottom of the list, heading 2 entering the strip jumped it back up,
     and leaving the strip jumped it down again. That is the jumping.

     The observer is now only a cheap trigger: every time any heading crosses
     the line, recompute which heading the reader is actually under by walking
     their positions. That is O(headings) on an event that fires a handful of
     times per article, not per scroll frame, so it keeps the INP argument that
     made an observer the right choice in the first place. */
  if (!('IntersectionObserver' in window)) return;

  var current;

  function mark(item) {
    if (current === item) return;
    if (current) current.a.classList.remove('is-on');
    current = item;
    if (current) current.a.classList.add('is-on');
  }

  /* The trip line: just under the sticky header, matching the scroll-margin
     above so a clicked link lands on the heading it just lit. */
  var line = headerH + 24;

  function update() {
    var active = null;
    for (var i = 0; i < links.length; i++) {
      /* <= line, so a heading sitting exactly on the line counts as reached.
         The last one that qualifies wins: that is the section being read. */
      if (links[i].h.getBoundingClientRect().top <= line + 1) active = links[i];
    }
    /* Nothing lit above the first heading — the reader is still in the intro
       and highlighting section 1 there would be wrong. */
    mark(active);
  }

  var io = new IntersectionObserver(update, {
    /* Bottom margin -1px rather than -70%: the band only has to be crossed to
       fire the recompute, and a tall band means headings drift in and out of
       it during momentum scrolling, firing far more often than needed. */
    rootMargin: '-' + line + 'px 0px -1px 0px',
    threshold: 0
  });

  links.forEach(function (item) { io.observe(item.h); });

  /* One pass on load, so a page opened at a #fragment or restored mid-article
     lights the right item instead of waiting for the reader to scroll. */
  update();
}());

/* ==========================================================================
   COMMENTS: LOAD THE NEXT PAGE WHEN THE READER REACHES THE END OF THE LIST

   The comment list is server-paginated three at a time
   (`{% paginate article.comments by 3 %}`) and the section prints the next
   page as an EMPTY <a> inside `.js-pagination` — markup that renders nothing
   and is meant to be read by script, not clicked.

   On live that script is Ajaxinate, constructed by theme.js on
   DOMContentLoaded against `.js-pagination-content` / `.js-pagination`.
   Redesigned pages load neither vendor.js nor theme.js, so nothing bound the
   hook and every post stopped dead at its first three comments — 3 of 75 on
   the stage-1-to-stage-2 guide. Sam reported it 8 Sep 2026.

   This is Ajaxinate's scroll mode and nothing more: same trigger, same three
   at a time, same markup. It is reimplemented here rather than fixed by
   loading theme.js, which would drag jQuery and 165KB of Apollo plugins back
   onto every article for one behaviour.

   The response is the whole article page — that is what the pagination link
   points at, and what Ajaxinate fetched too. Only the list items and the
   pagination link are taken out of it.
   ========================================================================== */
(function () {
  'use strict';

  var list = document.querySelector('.rd-comments.js-pagination-content');
  var pager = document.querySelector('.js-pagination');
  if (!list || !pager || !('IntersectionObserver' in window)) return;

  var busy = false;
  var failures = 0;

  function nextUrl() {
    var a = pager.querySelector('a[href]');
    return a ? a.getAttribute('href') : null;
  }

  function done() {
    io.disconnect();
    pager.innerHTML = '';
  }

  function load() {
    var url = nextUrl();
    if (busy || !url) return;
    busy = true;

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var more = doc.querySelector('.rd-comments.js-pagination-content');
        var nextPager = doc.querySelector('.js-pagination');
        if (!more) throw new Error('no list in response');

        /* Moving the nodes rather than copying innerHTML keeps each comment's
           id, which is what a /blogs/...#12345 link from a notification email
           lands on. */
        while (more.firstElementChild) list.appendChild(more.firstElementChild);

        pager.innerHTML = nextPager ? nextPager.innerHTML : '';
        busy = false;
        failures = 0;
        if (!nextUrl()) { done(); return; }

        /* RE-ARM. IntersectionObserver reports threshold CROSSINGS, and the
           pager does not move out of view when three comments are inserted
           above it — so without this the callback never fires a second time
           and the list stops at six. Re-observing queues a fresh initial
           report, which fires again if the pager is still in view and stays
           quiet once the reader has scrolled past it. */
        io.unobserve(pager);
        io.observe(pager);
      })
      .catch(function () {
        /* Leave the link in place: a later scroll retries. Give up after two
           so a dead endpoint cannot spin. */
        busy = false;
        if (++failures >= 2) done();
      });
  }

  /* 200px early, so the next three are usually there by the time the reader
     arrives rather than after a visible pause. */
  var io = new IntersectionObserver(function (entries) {
    if (entries.some(function (e) { return e.isIntersecting; })) load();
  }, { rootMargin: '0px 0px 200px 0px', threshold: 0 });

  if (nextUrl()) io.observe(pager); else pager.innerHTML = '';
}());

/* ---- back from Shopify's ?comment= 404 --------------------------------
   A comment posted to the moderated blog is redirected to
   ?comment=<id>#comments, which Shopify answers 404 while the comment waits
   for approval — see templates/404.liquid, which is what sends the visitor
   back here. The comment was accepted; say so where the form is, using the
   same line the server prints when it can render that state itself. */
(function () {
  var note = document.querySelector('[data-rd-comment-pending]');
  if (!note) return;

  var path;
  try {
    path = sessionStorage.getItem('rd-comment-pending');
    if (path === location.pathname) sessionStorage.removeItem('rd-comment-pending');
  } catch (e) { return; }
  if (path !== location.pathname) return;

  note.hidden = false;
  /* The scroll restorer may have put us back where the form was, and the
     #comments anchor only exists once the article HAS comments — so aim at
     the notice itself, which is in the form either way. */
  note.scrollIntoView({ block: 'center' });
}());
